import { z } from 'zod';
import { TenantId } from './ids.js';
import { CurrencyCode, Timezone } from './primitives.js';
import { DEFAULT_LOCALE, Locale } from './i18n.js';
import { TenantPaymentsConfig, paymentsConfigOf, paymentsUsable } from './payments.js';
import { ReservationPayment } from './reservations.js';
import { TenantFulfillmentConfig } from './fulfillment.js';
import { NewsletterConfig } from './newsletter.js';
import { GuestMessagingOwner, LocalTimeOfDay, TenantOwnerAlertsConfig, TenantRemindersConfig, } from './messaging.js';
import { BufferMinutes, MinLeadMinutes, TenantOfferPolicy } from './scheduling-policy.js';
import { TaxConfig } from './money.js';
import { BUTTON_FILLS } from './button-fill.js';
/**
 * Where a tenant is in its life. This enum is load-bearing: `authority` on every
 * availability response is derived from it (spec 4.4), so it is the difference
 * between an agent that promises a time and an agent that hedges.
 */
export const TenantStatus = z.enum([
    'onboarding', // being set up; the partner is not connected yet
    'importing', // dual-running with the old system during cutover
    'live', // we hold the book
    'paused', // temporarily not answering
    'offboarded',
]);
export const Tenant = z.object({
    id: TenantId,
    slug: z.string().min(1),
    name: z.string().min(1),
    timezone: Timezone,
    currency: CurrencyCode,
    status: TenantStatus,
});
/**
 * What the partner may rely on (agreement section 3). Every field is **computed**,
 * never stored as free text: the agreement is explicit that declaring a
 * capability you do not have is the only way to break the integration, and the
 * reliable way to avoid that is to make the declaration a function of reality.
 */
export const Capabilities = z.object({
    availability: z.enum(['live', 'approximate', 'none']),
    reserve: z.enum(['direct', 'link', 'none']),
    confirm: z.enum(['instant', 'webhook', 'email']),
    clients: z.enum(['full', 'basic', 'none']),
    cancel: z.enum(['direct', 'link', 'none']),
    /**
     * The payments facet (spec §3.7, §5.1.1 tier 3) — the first module facet to
     * arrive with a capability field of its own.
     *
     * `.default('none')` rather than a required field, and the default is the
     * load-bearing part: absence means `none`, which is the rule §5.1.1 fixed for
     * every tier-3 addition ("a simple concierge reads five fields and uses three
     * endpoints"). A consumer parsing an older provider's answer gets `none` and
     * routes around it, which is exactly the degradation the agreement designed.
     */
    payments: z.enum(['links', 'none']).default('none'),
    /**
     * The ordering facet (spec §3.6, agreement v1.4 §1.2) — "may this caller
     * place an order?", `"direct"` or `"none"`.
     *
     * It arrived on `provider-api`'s own `Entitlements` because that wave's scope
     * stopped at the service, and it lived beside `Capabilities` rather than in
     * it for one wave. That is the wrong home: `Capabilities` is *the* declaration
     * every surface shares, and a facet that is projected exactly like the other
     * five while being spread in from somewhere else is a seam nobody can see on
     * the wire and everybody has to remember in the code. So it is a field here,
     * and `routes/capabilities.ts` renders one object again.
     *
     * `.default('none')` for the reason `payments` has it, and the reason is the
     * same tier rule (§5.1.1): a consumer parsing an older provider's answer gets
     * `none` and routes around it. The field's **third** input — the calling key's
     * `orders:write` scope — is not this function's to know; it lives in
     * `provider-api`'s projection beside the module axis, exactly as the four
     * gates on `payments` do.
     */
    ordering: z.enum(['direct', 'none']).default('none'),
});
/**
 * `live` only when this system holds the book. `importing` means another system
 * can still take a booking, so anything we say may already be stale — the agent
 * should hedge, and it will, because we told it the truth.
 */
export function authorityFor(status) {
    return status === 'live' ? 'live' : 'approximate';
}
/**
 * The capability declaration for a tenant.
 *
 * `reserve` degrades with the tenant's state rather than being switched off by
 * hand: a paused or offboarded tenant cannot be booked into, and the agent
 * routes around it instead of failing mid-conversation.
 */
export function capabilitiesFor(status) {
    const bookable = status === 'live' || status === 'importing';
    return {
        availability: status === 'paused' || status === 'offboarded' ? 'none' : authorityFor(status),
        reserve: bookable ? 'direct' : 'none',
        confirm: 'instant',
        clients: status === 'offboarded' ? 'none' : 'full',
        cancel: bookable ? 'direct' : 'none',
        // **`live` only**, and deliberately stricter than `reserve`. An `importing`
        // tenant is dual-running with the system it is leaving (spec §3, §9), so a
        // deposit taken here is money the other system does not know about — and
        // §7.4's four dates of money are exactly what the cutover reconciliation
        // gates on. A booking taken during a dual-run can be reconciled; money
        // taken during one has to be found by hand.
        payments: status === 'live' ? 'links' : 'none',
        // **`reserve`'s axis, not `payments`'** — an order is a commitment: a
        // kitchen starts cooking on `placed`. So a paused or offboarded tenant must
        // not take one, and a dual-running tenant still can, because its tickets
        // reconcile the way its bookings do; §9's problem is money, which is why
        // `payments` above is stricter and this is not.
        ordering: bookable ? 'direct' : 'none',
    };
}
/* ------------------------------------------------------------------ */
/* The staff PWA's named set (docs/lovespa-personality.md)             */
/* ------------------------------------------------------------------ */
/**
 * A hex colour, and only a hex colour.
 *
 * The guest theme contract admits `rgb()`, `hsl()`, `oklch()` and `color-mix()`
 * because a tenant authors that document through an editor with a live preview.
 * The staff set is deliberately narrower: it is **four values and three
 * choices**, it is written by an operator, and the accent has a *derived* wash
 * computed from it — which needs a value this code can take apart. `#c19a3f` is
 * the whole vocabulary a support screenshot has to stay recognisable through.
 */
const HexColor = z
    .string()
    .regex(/^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i, 'must be a hex colour like #c19a3f');
/**
 * An image reference. `https:` only — the PWA is served over TLS and an `http:`
 * background would be mixed content that renders as nothing. No `data:` here
 * (unlike the guest contract): these are two full-bleed photographs, and a
 * base64 JPEG in a JSONB column read on every session is not a thing to invite.
 */
const HttpsImageUrl = z
    .string()
    .max(2000)
    .refine((value) => {
    try {
        return new URL(value).protocol === 'https:';
    }
    catch {
        return false;
    }
}, 'must be an https: URL');
/**
 * **The named set** — spec §6.4.3's staff-PWA rung, as
 * [`docs/lovespa-personality.md`](../../../docs/lovespa-personality.md)
 * resolves it.
 *
 * §6.4.3 says the staff PWA "deliberately stops at rung 2 (logo and accent)",
 * and the *principle* behind that sentence survives intact: consistency is what
 * makes support possible, so there is no arbitrary CSS here, no stylesheet slot,
 * no fork of the bundle for one tenant. What the personality doc found is that
 * the **list** was too short — the owner of LoveSpa went a long way to make the
 * app feel like itself, and four of the things they did are not expressible as
 * "a logo and an accent":
 *
 *  - the warm cream ground under an orientation-aware background photograph;
 *  - Georgia/Palatino for headings and the wordmark;
 *  - the wordmark itself: first word large in gold serif, second between hearts;
 *  - **the + button is a gold-gradient heart**, and the empty-state copy says so
 *    ("issue one with the heart button").
 *
 * So: rung 2 **plus a small named set**. Eight fields, all optional, every one a
 * *choice from a list this bundle ships* rather than a value that could be
 * anything. A named set keeps every support screenshot recognisable — a screen
 * is one of `2 × 2 × 2 × 3` shapes in two colours, not an unbounded space — and it
 * keeps "the shared bundle is never edited for one tenant" mechanical rather
 * than aspirational, the same way `apps/site`'s token table does for guests.
 *
 * **Absent means today's look, byte for byte.** Every field is `.optional()` and
 * the PWA writes nothing at all for one it does not have, so a tenant with no
 * `branding_staff` renders exactly the DOM it rendered before this field
 * existed. There is a test that asserts that sentence rather than trusting it.
 *
 * The eighth, `buttons`, was that decision (B9, 2026-09-26). Adding a ninth
 * is a decision too, not a patch: the set is the interface,
 * and the personality doc's own rule is "the names above are the whole set until
 * a real tenant needs a sixth".
 */
export const StaffBranding = z.object({
    /** LoveSpa's `--gold`, `#c19a3f`. The soft wash beside it is derived, not set. */
    accent: HexColor.optional(),
    /** The page ground under the photograph. LoveSpa's cream is `#fbf1e8`. */
    ground: HexColor.optional(),
    /** Shown in portrait — LoveSpa's `Background_Vertical.jpg`. */
    background_portrait_url: HttpsImageUrl.optional(),
    /** Shown in landscape, by media query, exactly as LoveSpa's own CSS does it. */
    background_landscape_url: HttpsImageUrl.optional(),
    /** `serif` is Georgia / Palatino Linotype — headings and the wordmark. */
    font_display: z.enum(['system', 'serif']).optional(),
    /** `hearts` is `LOVE / ♥ spa ♥ / frenchtown`; `plain` is the tenant's name. */
    brandmark: z.enum(['plain', 'hearts']).optional(),
    /** `heart` turns every add/walk-in affordance into the gold-gradient heart. */
    action_icon: z.enum(['plus', 'heart']).optional(),
    /**
     * The primary buttons' fill — B9's "alive buttons" (the owner, 2026-09-26):
     * `solid` is today's; `gradient` runs the accent into a derived second stop
     * (`buttonGradient`, which also picks a label that clears AA at both);
     * `animated` lets that gradient drift slowly and makes the gold heart's own
     * gradient drift with it. Still under `prefers-reduced-motion`.
     */
    buttons: z.enum(BUTTON_FILLS).optional(),
});
/** The set, as a list — so a console can render controls from it and a test can count it. */
export const STAFF_BRANDING_FIELDS = [
    'accent',
    'ground',
    'background_portrait_url',
    'background_landscape_url',
    'font_display',
    'brandmark',
    'action_icon',
    'buttons',
];
/**
 * Read `tenant_config.config.branding_staff` from a document that may predate
 * the field, or may have been hand-edited by an operator with `psql`.
 *
 * The same shape and the same reasoning as `defaultLocaleOf` above: the store
 * *casts* `tenant_config.config` rather than parsing it, so every reader needs
 * one answer to "what if it is not there, or not what it should be". One
 * function, rather than a `??` in each of them.
 *
 * **Field-by-field rather than all-or-nothing.** A document whose
 * `background_portrait_url` is `http:` still yields the accent and the heart:
 * one bad value costs its own field and nothing else. That is `resolveTheme`'s
 * rule ("ignore what you do not understand, keep what you had") applied one
 * surface over — and it matters more here, because the consumer is a counter
 * iPad and the alternative failure is a staff app that suddenly looks generic
 * on the morning of cutover.
 *
 * `null` — not `{}` — when there is nothing usable, so a caller can tell "this
 * tenant has no staff branding" from "this tenant chose the plus button".
 */
export function staffBrandingOf(config) {
    const raw = config?.['branding_staff'];
    if (raw === null || typeof raw !== 'object' || Array.isArray(raw))
        return null;
    const record = raw;
    const out = {};
    for (const key of STAFF_BRANDING_FIELDS) {
        const value = record[key];
        if (value === undefined)
            continue;
        if (StaffBranding.shape[key].safeParse(value).success)
            out[key] = value;
    }
    const parsed = StaffBranding.safeParse(out);
    if (!parsed.success)
        return null;
    return Object.keys(parsed.data).length === 0 ? null : parsed.data;
}
/**
 * The accent's **soft wash**, derived rather than configured.
 *
 * LoveSpa's stylesheet carries both: `--gold: #c19a3f` and
 * `--gold-soft: rgba(201, 162, 75, 0.16)`. The personality doc's inventory row
 * is deliberately "staff-branding token `accent` **+ derived soft**" — one value
 * an operator picks, one this function computes — because the two are not
 * independent decisions. A wash that does not match its accent looks like a bug,
 * and a second colour field is a second thing to set wrong on a screen support
 * has to recognise.
 *
 * (That also makes the guest side's opposite call legible: over there
 * `color.accent-ink` *is* a token, because contrast against an accent is a
 * decision a designer makes and no browser guesses it. A 16% wash of the same
 * hue is arithmetic.)
 *
 * Returns `null` for anything that is not a hex colour, so a caller writes the
 * property only when it has one.
 */
export function accentSoftWash(accent, alpha = 0.16) {
    if (accent === undefined)
        return null;
    const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(accent)?.[1];
    if (hex === undefined)
        return null;
    const full = hex.length === 3
        ? hex
            .split('')
            .map((c) => c + c)
            .join('')
        : hex;
    const r = Number.parseInt(full.slice(0, 2), 16);
    const g = Number.parseInt(full.slice(2, 4), 16);
    const b = Number.parseInt(full.slice(4, 6), 16);
    return `rgba(${String(r)}, ${String(g)}, ${String(b)}, ${String(alpha)})`;
}
/* ------------------------------------------------------------------ */
/* When a takeaway order is for (spec §3.6)                            */
/* ------------------------------------------------------------------ */
/**
 * **What "pickup" means at this restaurant**, and it is the restaurant's
 * choice rather than the platform's.
 *
 * `placeOrder` has taken `pickupAt: null` since §3.6 — "as soon as it is
 * ready" — and the guest page nonetheless asked every guest for a date and a
 * time, because a nullable column reads as an optional field. For a counter
 * that cooks to order that is a question nobody wants to answer: the honest
 * answer is *now*, and a date picker in front of it is friction invented by
 * the software.
 *
 *  - `asap` — no date, no time, anywhere. The request carries `pickup_at:
 *    null` and a request that carries a time is refused. **The default**,
 *    because it is what a takeaway counter is, and because a tenant that has
 *    never been configured must get the thing most of them want.
 *  - `scheduled` — a time is required; the caterer and the pizza place taking
 *    tomorrow's collection live here.
 *  - `either` — both, with "as soon as possible" preselected.
 *
 * It is a scalar on the config document rather than an `ordering: {…}` block
 * for the reason `slot_grid_min` is one: it is a single knob about how the
 * book behaves, and a block of one field is a shape to migrate the first time
 * a second field wants to live somewhere else.
 */
export const OrderTiming = z.enum(['asap', 'scheduled', 'either']);
/**
 * **The kitchen's own controls** — spec §3.6's confirmation policy and the
 * prep-time estimate it promises (`docs/verticals-plan.md` R4, workstream V1).
 *
 * `order_confirmation`:
 *  - `staff_confirm` — the default, and §3.6's stated one: a ticket lands
 *    `placed` and a person promises a time. A kitchen that has not looked at
 *    the pass has not promised anything, and a text saying "ready about 6:20"
 *    that nobody stands behind is the message §3.6 warns about — *"an API that
 *    accepts orders nobody notices is worse than no API."*
 *  - `auto_confirm` — the counter that always says yes: `placeOrder` moves the
 *    ticket `placed → confirmed` inside the transaction that wrote it, with no
 *    human, and stamps `default_prep_minutes` on it. A kitchen chooses this
 *    when its prep time is a fact about the kitchen rather than about tonight.
 *
 * `default_prep_minutes` is what the confirm control is **prefilled** with and
 * what `auto_confirm` writes. It is optional and has **no default**: an
 * estimate the software invented is exactly the promise nobody made, so a
 * tenant that has never said one confirms with no time and texts nothing.
 *
 * A **block** rather than two scalars beside `order_timing`, and the reason is
 * `till`'s rather than a change of mind: these two are one form on one card
 * (the console's Settings PUT, `yourstore ordering policy`), they are read by
 * one function, and the next question a kitchen asks ("how many tickets may
 * be open at once") lands beside them rather than as a fourth `order_*` scalar.
 *
 * **This block derives no `policies` row.** `derivedPolicies` states the rules
 * the profile tells a *guest* — the lead time, the hold, the deposit, the
 * waiver — and how a kitchen confirms a ticket is not one of them: the guest
 * learns a prep time on the ticket she gets, not from a rule published in
 * advance. So writing it moves `profile_version` for nothing, which is exactly
 * what `bumpProfileVersionIfRulesChanged` is there to notice.
 */
export const OrderConfirmation = z.enum(['staff_confirm', 'auto_confirm']);
/**
 * Twelve hours. A prep time is minutes from "confirmed" to "ready about", and
 * anything longer is a scheduled pickup (`order_timing`), which is a different
 * fact on a different column (`orders.pickup_at`).
 */
export const PREP_MINUTES_MAX = 720;
/**
 * A prep time, in minutes. `0` is a legal value and means *ready now* — a
 * drink poured at the counter — which is a different fact from "nobody set
 * one", and that difference is why `orders.prep_minutes` is nullable rather
 * than defaulting to zero (`schema/ordering.ts`).
 */
export const PrepMinutes = z.number().int().min(0).max(PREP_MINUTES_MAX);
export const OrderingPolicy = z.object({
    order_confirmation: OrderConfirmation.optional(),
    default_prep_minutes: PrepMinutes.optional(),
});
/**
 * What a writer may say about the block: each field set, or `null` to clear
 * it. `SchedulingPolicyPatch`'s shape, for its reason — the CLI, the console's
 * Settings PUT and `setOrderingPolicy` all take this one object, so "clear the
 * default prep time" is sayable on every surface and means one thing.
 */
export const OrderingPolicyPatch = z
    .object({
    order_confirmation: OrderConfirmation.nullable().optional(),
    default_prep_minutes: PrepMinutes.nullable().optional(),
})
    .strict();
export const ORDERING_POLICY_KEYS = ['order_confirmation', 'default_prep_minutes'];
/**
 * **The waiver a tenant publishes** — the owner's ruling (6) on
 * `docs/plan-2026-09-21.md`, answering `docs/verticals-plan.md` D3 / V-D2: a
 * departure's waiver is *a ticked box with a timestamp and the text version*,
 * not an e-signature. There is no vendor behind this type and there is not
 * meant to be one.
 *
 * Three fields, and the third is the one that earns the type:
 *
 *  - `title` — what the box is called above the tick ("Liability release").
 *  - `text` — the words themselves. One string, plain text, shown whole.
 *  - `version` — an integer the operator **bumps on any edit to the text**.
 *
 * ## Why a block in `tenant_config` and not the branding `policies` rows
 *
 * A `policies` row is prose on a page and may be rewritten at any moment
 * without anybody noticing; a waiver has to answer "which words did she agree
 * to" years later, so it needs an edition. `version` is that edition, and the
 * acknowledgement stores it beside a SHA-256 of the exact text, which is what
 * survives an operator editing the words and forgetting to bump the number.
 *
 * ## Why the text is one string and not a localized map
 *
 * Every other tenant-visible string in this system is a localized map, and this
 * one is deliberately not. A translated waiver is a **different document** —
 * different words, a different thing agreed to — and a fallback ladder that
 * quietly served the English text to a Spanish guest because the Spanish one
 * was missing is exactly the behaviour a record of consent must not have. A
 * tenant who needs two languages publishes the text bilingually, in one string,
 * and both halves are hashed together. Making this localized is a decision
 * (with a locale column on the acknowledgement beside it), not a patch.
 */
export const TenantWaiver = z.object({
    title: z.string().min(1).max(200),
    text: z.string().min(1).max(20_000),
    /** Bumped on **any** edit to `text`. An acknowledgement stores the number it saw. */
    version: z.number().int().min(1),
});
/* ------------------------------------------------------------------ */
/* How the till was paid (docs/lovespa-gaps-plan.md B4)                */
/* ------------------------------------------------------------------ */
/**
 * **The named external tenders** — what settles the remainder of a settlement
 * once the waterfall's instruments have had their turn (spec §6.5).
 *
 * They are **recorded, never processed**. Nothing in this platform moves any of
 * this money: there is no card rail, no Venmo API, no PayPal charge behind a
 * `paypal` tender. The owner of `docs/lovespa-discovery.md` §4 dropped cards on
 * purpose after four years of ≈6 % plus terminal rental, and takes *cash, check,
 * Venmo and PayPal*; what she needs is a till that writes down which one it was,
 * not a processor. Platform payment links exist and run on PayPal Orders with
 * merchant fees attached — `paypal` here is emphatically **not** that, and
 * routing her day-to-day tender through one would be charging her a fee she
 * currently does not pay.
 *
 * `cash`, `card` and `other` are the three spec §6.5 shipped with and they keep
 * their spelling, so every settlement ever written stays legible. The three new
 * ones come out of the same sentence in the room: *"Venmo, check and PayPal are
 * all 'other', unnamed"* was the gap, and a year of revenue that cannot say how
 * it arrived is a year the owner cannot reconcile against four apps.
 *
 * `other` survives *because* the list is closed: Zelle, Cash App, a bank
 * transfer and whatever arrives next year are real, and the honest place for
 * them is one kind plus a short label the owner types (`SettlementTender.label`,
 * ≤ 40 characters) rather than a fleet-wide enum that grows by one every time a
 * single tenant meets a new app.
 */
export const EXTERNAL_TENDERS = ['cash', 'check', 'venmo', 'paypal', 'card', 'other'];
export const ExternalTender = z.enum(EXTERNAL_TENDERS);
/** The longest a `other` tender's free-text label may be. */
export const TENDER_LABEL_MAX = 40;
/**
 * **How a refund went back** (`docs/money-second-pass-design.md` §4).
 *
 * The six recorded external tenders — money physically out of a drawer, an app
 * or a bank — plus two that keep the money with the shop:
 *
 *  - `credit_cash` puts it back on the guest's account as spendable cash credit.
 *    The refund reduces revenue by X and the positive `cash` credit entry
 *    recognizes X on the same day, so the net is zero — which is right, because
 *    the shop still has the money.
 *  - `credit_gift` returns **goodwill**. §7.4 rule 2: gift credit was never
 *    income, it entered as goodwill and left as a discount, and handing back
 *    cash for it would turn a giveaway into a withdrawal.
 *
 * `deposit` and `certificate` are deliberately absent. A refund does not un-take
 * a deposit, and a certificate sale is refunded as its own document against its
 * own referent (`refunds.certificate_id`), which voids what is left on the card.
 *
 * It lives here rather than in `money.ts` because it is **tender vocabulary**,
 * and the one place the tender vocabulary is written down is next to
 * `EXTERNAL_TENDERS`.
 */
export const REFUND_TENDERS = [...EXTERNAL_TENDERS, 'credit_cash', 'credit_gift'];
export const RefundTender = z.enum(REFUND_TENDERS);
/** `true` when a refund tender is money leaving the shop rather than credit issued. */
export function refundTenderIsMoneyOut(kind) {
    return EXTERNAL_TENDERS.includes(kind);
}
/**
 * **Which tenders this tenant's till shows** — the buttons, in the order the
 * owner wants to meet them.
 *
 * The default is `cash · card · other`, which is *exactly* what every till in
 * the fleet has drawn since the checkout existed. That is the point: widening
 * the vocabulary must not, on its own, put three new buttons in front of forty
 * businesses that never asked for them. Love Spa sets `cash · check · venmo ·
 * paypal` and the card button she deliberately does not want simply is not
 * there — which is the only version of this she would recognise as help.
 *
 * It is a **display** rule and nothing more. The server accepts any
 * `ExternalTender` whatever this says, because a list of buttons is not an
 * authorization boundary and refusing a legitimately-recorded `card` at
 * settlement time would be this knob inventing a policy about money. A tender
 * the owner removes from the till tomorrow must not make yesterday's checkout
 * unreadable, and it does not.
 *
 * A **block** rather than `order_timing`'s scalar, and the difference is the
 * shape of the value rather than a change of mind: this is a *list*, the till is
 * a surface with more than one question already waiting for it (which tenders,
 * whether tax is shown, what the receipt says), and `till_tenders` as a bare
 * array would be the first of several `till_*` scalars pretending not to be a
 * block. Read through `tillTendersOf`, which is where "the document predates the
 * field" is answered once.
 */
export const TillConfig = z.object({
    tenders: z.array(ExternalTender).min(1).max(EXTERNAL_TENDERS.length).optional(),
    /**
     * **Whether the till asks for a tip** — default `false`, and the default is
     * the decision rather than a placeholder.
     *
     * A solo owner working alone tips nobody, and a register that puts three
     * percentage chips in front of every guest she has known for nine years is the
     * software making a suggestion on her behalf that she never asked it to make.
     * The first real tenant is exactly that shop. A spa with therapists turns it
     * on, and the field is there so that is one word rather than a fork.
     *
     * It is a **display** rule like `tenders`: the server records a `tip_cents`
     * whatever this says, because a settlement written the day before the owner
     * changed her mind must stay recordable and readable.
     */
    tips: z.boolean().optional(),
});
/** What a tenant that has never been asked shows — today's three, unchanged. */
export const DEFAULT_TILL_TENDERS = ['cash', 'card', 'other'];
/* ------------------------------------------------------------------ */
/* How a guest gets an appointment (docs/lovespa-discovery.md §2.3)    */
/* ------------------------------------------------------------------ */
/**
 * **Whether a stranger may book themselves**, and it is the business's answer
 * rather than the platform's.
 *
 * The real spa owner of `docs/lovespa-discovery.md` §2.3 admits new people
 * personally: *"I need to gently bring the new people in"* — trusted clients
 * may one day book themselves, strangers **request** and she confirms
 * (*"I like that better"*). Her demonstration page nonetheless carried an open
 * public booking card, which is the one thing on it that contradicted what she
 * told us.
 *
 *  - `open` — anyone who proves a phone number may book any `bookable_online`
 *    item, which is what every surface has done since the guest site existed.
 *    **The default**, because it is what every tenant is configured as today
 *    and a rollout that changed one of them would be this knob inventing a
 *    policy nobody chose.
 *  - `request` — the guest site takes no booking at all. Every entry point on
 *    the page becomes a *request* (the `inquiry` route, carrying the treatment
 *    and when suits them), and `POST /api/bookings` is refused by the server
 *    with `unsupported`. Availability stays readable, because a request may
 *    name a preferred time; cancelling and reading one's own visits stay
 *    exactly as they were, because a client the owner booked in herself is
 *    still that client's appointment.
 *
 * **This is a guest-site rule and lives only there.** The provider agreement's
 * caller is the partner's agent and the staff surface is the owner's own hand;
 * neither is a stranger, and neither is narrowed by this. `order_timing` above
 * set the same precedent for the same reason — `apps/guest-api` enforces it and
 * `packages/store` does not, because a rule in the repo would have quietly
 * amended a partner contract.
 *
 * A scalar on the config document rather than a `guest: {…}` block, for the
 * reason `order_timing` is one: a single knob about how the book behaves, and a
 * block of one field is a shape to migrate the first time a second field wants
 * to live somewhere else.
 *
 * ## Three values, and `trusted` is the one she actually asked for (A5)
 *
 * The interim shipped two of them and said so: *"until those exist the page
 * asks **everybody**, which is the wrong half of her rule applied safely rather
 * than the right rule applied wrongly."* `trusted` is the right rule —
 * *"trusted clients book themselves; strangers request and she confirms"* — and
 * it is a **third enum value rather than a second flag**, because the three are
 * mutually exclusive answers to one question and a pair of booleans would have
 * had a fourth state nobody can describe.
 *
 * | mode | a client with `may_book_online` | everybody else |
 * | --- | --- | --- |
 * | `open` | books | books |
 * | `trusted` | books | requests |
 * | `request` | requests | requests |
 *
 * `request` keeps exactly the meaning it shipped with, so no tenant that set it
 * changes behaviour when this value arrives; `open` stays the default, so no
 * tenant that never set it changes either. The per-client half of the table is
 * `clients.may_book_online` (migration 0040), which defaults to `false` — an
 * imported client is not yet a trusted one, because trust is something the
 * owner says rather than something a CSV implies.
 */
export const GuestBooking = z.enum(['open', 'trusted', 'request']);
/**
 * **Who may change what the business sells** — B10
 * (`docs/console-redesign-plan.md` §3; the owner, 2026-09-26: *"maybe just some
 * of the staff roles can add/remove products while others can't"*).
 *
 * The roles stay three (`owner`, `staff`, `readonly`); this is the one
 * per-business answer that decides where the catalog line falls between them:
 *
 * | value | owner | staff | readonly |
 * | --- | --- | --- | --- |
 * | `owner` (default) | changes | reads | reads |
 * | `owner_and_staff` | changes | changes | reads |
 *
 * "Changes" is every catalog write the staff app makes — a product or a
 * treatment created, edited, retired, its options and add-ons, and its
 * photographs — and `core-api` enforces it (`catalog: true` in its route
 * registry). `owner` is the default because it is what photographs already
 * were (B7: "the owner's, and only the owner's"), and a business that has
 * never been asked should not find its front desk renaming the menu.
 *
 * A third value — per-seat lists, "Maria may but Ana may not" — was the
 * alternative, and it is a fourth role in all but name. The owner's own
 * sentence is "some of the staff roles", and there is one staff role.
 */
export const CatalogEditors = z.enum(['owner', 'owner_and_staff']);
/**
 * **How long an unconfirmed request holds the time it asked for**, in hours.
 *
 * A request occupies `appointment_resources` exactly as a confirmed booking
 * does — otherwise two people are promised the same hour — so it is *stock of
 * time* taken off the shelf, and stock taken off a shelf indefinitely by
 * somebody who never came back is stock lost. 48 hours is the number the owner
 * described working to (she answers at the end of the business day and does not
 * text on Sundays, so a Friday-evening request has to survive the weekend); the
 * ceiling is a week, past which the guest has certainly made other plans.
 *
 * Expiry itself is a **comparison, not a job** — `photo_album_status`' rule and
 * `reservations`': availability treats a request past its hour as free the
 * instant it is past, and the sweep that writes `cancelled` is what sends the
 * text and tidies the row. A late sweep therefore costs a word on a screen, not
 * a slot.
 */
export const REQUEST_HOLD_HOURS_DEFAULT = 48;
export const REQUEST_HOLD_HOURS_MAX = 168;
export const RequestHoldHours = z
    .number()
    .int()
    .min(1)
    .max(REQUEST_HOLD_HOURS_MAX)
    .default(REQUEST_HOLD_HOURS_DEFAULT);
/* ------------------------------------------------------------------ */
/* What the shop does (docs/lovespa-gaps-plan.md C1)                   */
/* ------------------------------------------------------------------ */
/**
 * **What a guest may do with the things on a shop's shelf** — post them, collect
 * them, or both.
 *
 * Two booleans rather than an enum of three, because they are independent facts
 * about a business rather than three modes: a print shop posts and does not hand
 * anything over a counter, a spa hands everything over a counter and posts
 * nothing, and a surf shop one day does both. An enum would have had to grow a
 * fourth value the first time somebody wanted the combination.
 *
 * A block rather than two scalars, because unlike `order_timing` and
 * `guest_booking` these two are read *together* every time — "what can this shop
 * do" is one question — and because the shape has an obvious third member
 * waiting for it (delivery) that would otherwise arrive as a third loose key.
 *
 * The default is **today's behaviour, exactly**: `{ pickup: false, ship: true }`.
 * Every tenant with a shop has a posted one, and a rollout that turned pickup on
 * for all of them would be this knob inventing a product decision.
 */
export const ShopFulfilment = z.object({
    /** Reserve online, collect at the counter (`reservations.ts`). */
    pickup: z.boolean().default(false),
    /** The posted shop: a manufacturer's variants, a live postage quote, a pay link. */
    ship: z.boolean().default(true),
});
/* ------------------------------------------------------------------ */
/* The concierge integration's residue                                 */
/* ------------------------------------------------------------------ */
/**
 * What yourstore keeps after switching the concierge on for a tenant.
 *
 * Three fields, and the shape of the list is the design: **everything about how
 * the agent behaves lives in aiconcierge**, and everything about what the
 * business *is* lives here and reaches the agent through `GET /v1/profile` and
 * `GET /v1/services`. So this block deliberately holds no autonomy, no
 * greeting, no tone and no per-service switch — those would be a second copy of
 * a fact somebody else owns, which is the incident the whole integration exists
 * to end (`../aiconcierge/docs/partner-provisioning-api.md` §1).
 *
 * What is left is the residue of the handshake: the public widget key, where
 * their console is, and when it was done.
 */
export const TenantConciergeConfig = z.object({
    /** aiconcierge's public chat-widget key for this tenant's site. Not a secret. */
    site_key: z.string().min(1),
    /** Where an owner finishes what only their console can do — a phone number. */
    console_url: z.string().optional(),
    /** When the enable sequence last completed. ISO-8601. */
    enabled_at: z.string().optional(),
});
/** Per-tenant scheduling knobs. Defaults are LoveSpa's, so the port is a no-op. */
export const TenantConfig = z.object({
    /** Start granularity for offered slots, in minutes. */
    slot_grid_min: z.number().int().min(5).max(120).default(15),
    /** How far ahead availability may be asked about. */
    horizon_days: z.number().int().min(1).max(365).default(60),
    /** Ceiling on a hold's requested TTL. */
    max_hold_seconds: z.number().int().min(60).max(3600).default(1800),
    /** How far `alternatives` may wander from the ask, in days. */
    alternatives_window_days: z.number().int().min(1).max(30).default(7),
    /** Monotonic; bumped by any write that changes the catalog (agreement S2). */
    catalog_version: z.number().int().min(0).default(1),
    /**
     * The language this tenant's content is written in when nothing else is said
     * (i18n-design §2, Domain B). It is what a localized map is keyed by on write
     * and what `localize` falls back to on read, so changing it does **not**
     * retranslate anything — it changes which language a guest who asked for
     * neither gets.
     */
    default_locale: Locale.default(DEFAULT_LOCALE),
    /**
     * The languages the tenant *offers*. Narrows negotiation — a browser asking
     * for French at a tenant that speaks English and Spanish is answered in one of
     * those two rather than being handed a French request the data cannot fill.
     *
     * Empty means "undeclared", which is not the same as "English only": the
     * catalog still decides, and a tenant who translated their menu before filling
     * in this row is served in the language they translated into.
     */
    supported_locales: z.array(Locale).default([]),
    /**
     * **Pacing** (spec §4.6, restaurant feature 4): the most covers that may
     * *start* in one `slot_grid_min` step, tenant-wide.
     *
     * "Tables being free is not the same as the kitchen surviving the burst."
     * Absent means no cap, which is every tenant that is not a restaurant — and
     * why it is `.optional()` with no default: a default of, say, 999 would be a
     * cap nobody chose, silently in force, and impossible to distinguish from one
     * an owner set. A per-item cap (`item_scheduling.max_starts_per_step`)
     * overrides this for that item; this is the floor-wide default.
     */
    max_starts_per_step: z.number().int().min(1).max(1000).optional(),
    /**
     * **The gap this business keeps between guests** — A2, and the owner's own
     * sentence: *"I do a half an hour between clients"*, always, every service.
     *
     * A buffer has been a property of an *item* since the engine existed, which
     * makes "always" a number typed onto twenty-one rows and remembered on the
     * twenty-second. These two are the house's answer, and the resolution is one
     * line: **an item's own non-zero buffer wins; otherwise the tenant's
     * default.** It lives in exactly one function — `buffersFor` in
     * `packages/scheduling` — so the offered span, the occupied span the
     * exclusion constraint sees, and the ledger that admits a slot cannot
     * disagree about it.
     *
     * `.optional()` and read through `bufferDefaultsOf`, which is
     * `max_starts_per_step`'s shape and `max_starts_per_step`'s reason read one
     * notch gentler: absent means *this house has not said*, the reader answers
     * `0`, and `0` is exactly today's behaviour for every document in the fleet.
     * Defaulting the field in the schema instead would have made every literal
     * `TenantConfig` in the repository state a gap it does not have.
     *
     * **An item at `null` takes the default; an item at `0` has none** (migration
     * 0064). Until then `item_scheduling.buffer_after_min` was `NOT NULL DEFAULT
     * 0`, so "explicitly none" and "never set" were the same bytes and the
     * resolver had to read `0` as "take this"; the column is nullable now and
     * `buffersFor` reads the two apart.
     */
    default_buffer_before_min: BufferMinutes.optional(),
    default_buffer_after_min: BufferMinutes.optional(),
    /**
     * **How far ahead the public may book** — A3. *"I like being able to tell
     * people I'm booked for two weeks."*
     *
     * `min_lead_minutes` is a floor in minutes and `same_day_cutoff` is a local
     * `HH:MM` after which today stops being offered at all — she answers texts at
     * the end of the business day, so a four-o'clock slot booked at half past
     * three is a slot nobody told her about. Both are applied exactly where
     * "never a time that has already gone" is applied today, in the tenant's own
     * zone, and **neither narrows a member of staff's own hand** (`house_rules`
     * on the engine's `SlotQuery`).
     *
     * Both `.optional()` and read through `leadTimeOf`, for
     * `default_buffer_after_min`'s reason: absent answers `0` and `null`, which
     * is today's behaviour for the whole fleet.
     */
    min_lead_minutes: MinLeadMinutes.optional(),
    same_day_cutoff: LocalTimeOfDay.optional(),
    /**
     * **Which of the free starts she actually wants named** — A4,
     * `TenantOfferPolicy` in `scheduling-policy.ts`, and the sentence the whole
     * feature exists for: *"I don't want to offer them the 11 o'clock, I want to
     * offer the 3 o'clock… I'm like a jigsaw puzzle how I fit people in."*
     *
     * `.optional()` and read through `offerPolicyOf`, because absent has to be
     * indistinguishable from the engine before this wave — `open`, unranked,
     * nothing protected — and because **we do not know which mode is hers**. The
     * operator console's offer simulator is how that gets decided, on a day out
     * of her own paper book, with her in the room.
     */
    offer_policy: TenantOfferPolicy.optional(),
    /**
     * **When a takeaway order is for** — `OrderTiming` above, `asap` by default.
     *
     * `.default('asap')` rather than `.optional()`, and the difference matters:
     * this is a knob with a right answer for a tenant who has never been asked,
     * so absence *is* a choice ("as soon as it's ready") rather than the absence
     * of one. `max_starts_per_step` above is optional for the opposite reason —
     * a default cap would be a cap nobody set.
     */
    order_timing: OrderTiming.default('asap'),
    /**
     * **How a ticket gets confirmed, and how long the kitchen says it takes** —
     * `OrderingPolicy` above. `.optional()` and read through `orderingPolicyOf`,
     * because absent has to mean *today's behaviour* (a person confirms, no
     * estimate) for every document written before this block existed, which is
     * every document in the fleet. Not a `policies` row — see the block's own
     * comment.
     */
    ordering: OrderingPolicy.optional(),
    /**
     * **How a guest gets an appointment** — `GuestBooking` above, `open` by
     * default.
     *
     * `.default('open')` rather than `.optional()`, for `order_timing`'s reason
     * and its converse: this is a knob with a right answer for a tenant who has
     * never been asked, and here that answer has to be *today's behaviour*. Every
     * tenant in the fleet takes bookings from its own site right now, and a
     * default of `request` would have switched all of them off in one commit.
     */
    guest_booking: GuestBooking.default('open'),
    /**
     * **Who may change what the business sells** — `CatalogEditors` above, read
     * through `catalogEditorsOf`. `.optional()` for `shop_fulfilment`'s reason:
     * the document is cast on the hot path, so absent has to be answered by one
     * function, and its answer is `owner`.
     */
    catalog_editors: CatalogEditors.optional(),
    /**
     * **How long an unconfirmed request holds its slot**, in hours —
     * `RequestHoldHours` above, 48 when absent.
     *
     * `.optional()` and read through `requestHoldHoursOf`, for `shop_fulfilment`'s
     * reason rather than `order_timing`'s: the document is cast rather than parsed
     * on the hot path, so a `.default()` would not put itself into an existing
     * row, and one function has to answer "what if it is not there". It is only
     * ever consulted by a tenant that takes requests, so every other tenant in the
     * fleet reads a field that is never asked for.
     *
     * **`.removeDefault()` before `.optional()`**, which is the correction the
     * integration pass made rather than a flourish. `RequestHoldHours` carries a
     * `.default(48)` because it is also the bound a *route* validates a typed
     * number against; leaving that default on here made `TenantConfig.parse({})`
     * return `{ request_hold_hours: 48 }` — a schema asserting that every tenant
     * in the fleet, including the ones whose sites simply book, holds requests for
     * two days. Absent has to survive parsing, or "never answered" and "answered
     * with the default" stop being different facts and `DEFAULT_CONFIG`'s whole
     * three-kind rule stops meaning anything.
     */
    request_hold_hours: RequestHoldHours.removeDefault().optional(),
    /**
     * **What a guest may do with the shop** — `ShopFulfilment` above, and
     * `docs/lovespa-gaps-plan.md` C1.
     *
     * `.optional()` and read through `shopFulfilmentOf`, for `payments`' reason
     * rather than `order_timing`'s: absent has to be *indistinguishable from the
     * fleet before this wave*, and this document is **cast rather than parsed** on
     * the hot path, so a `.default()` would not put itself into an existing row
     * anyway. One function answers "what if it is not there", and its answer is
     * `{ pickup: false, ship: true }` — today's shop, exactly.
     */
    shop_fulfilment: ShopFulfilment.optional(),
    /**
     * **Where a pickup reservation gets paid for** — `ReservationPayment` in
     * `reservations.ts`, read through `shopPaymentOf`.
     *
     * Absent is not a fixed word here, and that is the one thing about this field
     * worth reading twice: it answers `prepay` when the tenant has a usable
     * payment rail and `at_pickup` when it has none. A shop that cannot take a
     * payment online must not be configured, by omission, to demand one — and a
     * shop that *can* should not quietly stop asking because nobody typed a word
     * into a JSON document. The owner this arrived for sets it explicitly
     * (`at_pickup`), for the reason in `reservations.ts`.
     */
    shop_payment: ReservationPayment.optional(),
    /**
     * **How long a shelf holds a collected-by-nobody reservation**, in days after
     * it was marked ready. Read through `pickupExpiryDaysOf`; `7` when absent,
     * which is the number the owner and the operator agreed in the room.
     *
     * A scalar rather than a field on `shop_fulfilment`, for `order_timing`'s
     * reason: the block above is *what the shop offers*, and this is a house rule
     * about patience. It is read **once**, when a reservation becomes ready, and
     * stored on the row — see `reservationExpiryAt`.
     */
    shop_pickup_expiry_days: z.number().int().min(1).max(90).optional(),
    /**
     * **Which tenders the till draws buttons for** — `TillConfig` above, and
     * `docs/lovespa-gaps-plan.md` B4.
     *
     * `.optional()` and read through `tillTendersOf`, for `payments`' reason
     * rather than `order_timing`'s: absent has to be *indistinguishable from the
     * fleet before this wave*, and a `.default()` on a document that is **cast
     * rather than parsed** on the hot path would not put itself into an existing
     * row anyway. One function answers "what if it is not there", and its answer
     * is today's three buttons.
     */
    till: TillConfig.optional(),
    /**
     * **What this tenant charges in tax** — `TaxConfig` in `money.ts`, read
     * through `taxConfigOf`.
     *
     * `.optional()` and absent on every tenant today, for `till`'s reason plus one
     * of its own: the **US Virgin Islands has no general sales tax**, so the
     * correct value for the shop this platform was built for is "there is no such
     * block" rather than a rate of zero. The two are different facts — one hides
     * the tax line entirely, the other prints `$0.00` — and `taxConfigOf` returns
     * `null` for the first.
     */
    tax: TaxConfig.optional(),
    /**
     * The payments facet's per-tenant document (spec §3.7): the PayPal credential
     * *reference* and the deposit policy.
     *
     * It lives in this JSONB document rather than in a table of its own because
     * that is precisely what this document is — per-tenant policy — and because
     * every service that needs it already reads it (`getTenantSettings` is on
     * `provider-api`'s hold path and `core-api`'s catalog path). A table would
     * have bought a second set of RLS policies, two more grants and a repo, for
     * four scalars and a rule. What is *not* in here, ever, is a secret:
     * `secret_ref` names one, the way `webhook_endpoints.secret_ref` does.
     *
     * `.optional()` and read through `paymentsConfigOf`, because this document is
     * cast rather than parsed on the hot path and most tenants have no payments
     * block at all.
     */
    payments: TenantPaymentsConfig.optional(),
    /**
     * The fulfillment facet's per-tenant document (`docs/fulfillment-design.md`
     * decisions 2 and 4): the provider credential *reference*, the draft/confirm
     * flag, the inbound webhook token, and the fixed print list.
     *
     * It lives here for exactly the reasons `payments` does — this document *is*
     * per-tenant policy, every service that needs it already reads this column,
     * and a table would have bought a second set of RLS policies and two more
     * grants for four scalars and a list. What is *not* in here, ever, is a
     * secret: `api_key_ref` names one, the way `payments.secret_ref` does.
     *
     * `.optional()` and read through `fulfillmentConfigOf`, because this document
     * is cast rather than parsed on the hot path and almost no tenant sells merch.
     */
    fulfillment: TenantFulfillmentConfig.optional(),
    /**
     * The staff PWA's named set (`StaffBranding` above, and
     * `docs/lovespa-personality.md`).
     *
     * It lives in this document for the reason `payments` does and one more: the
     * PWA already reads `GET /v1/session` on every sync, `getTenantSettings`
     * already reads this column, and a table would have bought a second set of RLS
     * policies and two more grants for four scalars and three enums. What is
     * *not* in here is anything the guest side reads — `guest-api` projects
     * `config.branding` and nothing else, so a staff background photograph never
     * rides a stranger's phone.
     *
     * `.optional()` and read through `staffBrandingOf`, because this document is
     * cast rather than parsed on the hot path and **every tenant but one has no
     * staff branding at all** — which is the state that must render today's look
     * byte for byte.
     */
    branding_staff: StaffBranding.optional(),
    /**
     * **What this tenant's concierge integration left behind** — and the answer
     * to "where does `site_key` live".
     *
     * `site_key` is aiconcierge's *public* chat-widget key: the value a
     * `<script>` tag on the tenant's booking site carries, in the same class of
     * thing as a Firebase web API key. It is not a credential and it is
     * deliberately not a `secret_ref` — there is nothing to resolve, and putting
     * a public key behind Secret Manager would teach the wrong lesson about the
     * two fields beside it that *are* secrets and live in their Secret Manager
     * and ours.
     *
     * It is here rather than in a table of its own for the reasons `payments` and
     * `fulfillment` are: this document *is* per-tenant policy, every service that
     * would read it already reads this column, and a table would have bought a
     * second set of RLS policies and two more grants for three scalars. What is
     * **not** in here, ever, is the API key or the webhook signing secret — the
     * key exists only as a `api_keys` SHA-256 and in their Secret Manager, and
     * the signing secret is named by `webhook_endpoints.secret_ref` exactly as
     * every other one is.
     *
     * `.optional()` and read through `conciergeConfigOf`, because this document
     * is cast rather than parsed on the hot path and almost no tenant has a
     * concierge yet.
     */
    concierge: TenantConciergeConfig.optional(),
    /**
     * **Who owns guest messaging for this tenant** — spec §2.6, by the name §2.6
     * gives it. `yourstore` when absent, which is the de-facto state: every
     * guest-facing message this platform sends today comes from here.
     *
     * A scalar rather than a block, for `order_timing`'s reason: it is one knob
     * with three values, and `messaging: { owner: … }` would be a shape to migrate
     * the first time a second field wanted to live somewhere else. The two blocks
     * below are the things that *are* documents.
     *
     * Read through `guestMessagingOf` (`messaging.ts`).
     */
    guest_messaging: GuestMessagingOwner.default('yourstore'),
    /**
     * **The waiver this tenant asks a guest to acknowledge before a sailing**
     * (owner's ruling 6, migration 0049). `.optional()` and read through
     * `waiverOf`, because absent means *no waiver is asked for* and absent is
     * what every tenant's document says today — including every tenant that will
     * never publish one, which is every spa and every restaurant.
     */
    waiver: TenantWaiver.optional(),
    /**
     * **Guest reminders** (`docs/lovespa-gaps-plan.md` E1) — whether, when, and
     * inside which local hours. `.optional()` and read through
     * `remindersConfigOf`, because absent means *off* and absent is what every
     * tenant's document says today.
     *
     * It is gated by `guest_messaging` and not only by its own `enabled`: §2.6's
     * rule is that a tenant whose partner owns the channel gets no guest SMS from
     * here at all. `remindersMaySendFrom` is the one place both halves are asked.
     */
    reminders: TenantRemindersConfig.optional(),
    /**
     * **Owner alerts** (`docs/lovespa-gaps-plan.md` E2) — the owner's own phone,
     * about the owner's own book. `.optional()`, off when absent.
     *
     * Deliberately **not** gated by `guest_messaging`: §2.6's table puts "staff
     * alerts" in the "always yourstore" row, and a message to the business about
     * its own diary is not a conversation with a guest. A partner owning the spa's
     * guest number changes nothing here.
     */
    owner_alerts: TenantOwnerAlertsConfig.optional(),
    /**
     * **The newsletter's small settings** (`docs/lovespa-gaps-plan.md` E3,
     * migration 0042) — the local part the mail is sent from, where replies go,
     * the postal address CAN-SPAM requires, and the pacing.
     *
     * It is here, and the reason is an integration-pass correction rather than a
     * design: `NewsletterConfig` shipped in `newsletter.ts` documenting itself as
     * living *"in `tenant_config.config.newsletter`"*, and `TenantConfig` — the
     * schema that is supposed to be the list of what that document may contain —
     * had never heard of it. The consequences were real rather than tidy:
     * `DEFAULT_CONFIG`'s three-kind taxonomy could not classify it, and
     * `admin-api`'s settings route is `.strict()`, so a console could not have been
     * extended to write it without this line existing first.
     *
     * `.optional()`, kind 3 (*"the answer is not ours to give"*): every field has a
     * named fallback in `newsletterConfigOf` and in the composer, and writing a
     * from-address into a new tenant's row would be the software asserting a
     * business fact nobody stated.
     */
    newsletter: NewsletterConfig.optional(),
});
/**
 * The tenant's default locale, read from a config document that may predate the
 * field.
 *
 * `tenant_config.config` is versioned JSONB and the store *casts* rather than
 * parses it (there is one shape today and re-parsing it on every availability
 * request would be work on the hot path for no obligation). So a config written
 * before migration 0012 has no `default_locale` at all, and every reader needs
 * the same answer to that. One function, rather than eleven `?? 'en'`s.
 */
export function defaultLocaleOf(config) {
    const raw = config?.default_locale;
    return typeof raw === 'string' && Locale.safeParse(raw).success ? raw : DEFAULT_LOCALE;
}
/**
 * When this tenant's takeaway orders are for, read from a config document that
 * predates the field — which is **every document written before this wave**.
 *
 * `defaultLocaleOf`'s shape and `defaultLocaleOf`'s reason: the store *casts*
 * `tenant_config.config` rather than parsing it, so every reader needs one
 * answer to "what if it is not there, or is not one of the three". `asap` is
 * that answer, which makes the rollout a no-op for every existing tenant and
 * the new behaviour the one they get.
 */
export function orderTimingOf(config) {
    const raw = config?.order_timing;
    return OrderTiming.safeParse(raw).success ? raw : 'asap';
}
/**
 * How this kitchen confirms a ticket, read from a config document that may
 * predate the block — which is **every document written before this wave**.
 *
 * `orderTimingOf`'s shape and `orderTimingOf`'s reason, applied field by field
 * rather than to the block whole (`tillTendersOf`'s tolerance): a block that
 * names a good confirmation mode and a prep time somebody typed as a string
 * yields the mode and no time, because throwing the owner's whole answer away
 * over one bad field is worse than either half of it. `staff_confirm` and
 * `null` are today's behaviour, so the rollout changes nothing for anybody.
 */
export function orderingPolicyOf(config) {
    const raw = config?.['ordering'];
    const block = (typeof raw === 'object' && raw !== null ? raw : {});
    const mode = OrderConfirmation.safeParse(block['order_confirmation']);
    const prep = PrepMinutes.safeParse(block['default_prep_minutes']);
    return {
        orderConfirmation: mode.success ? mode.data : 'staff_confirm',
        defaultPrepMinutes: prep.success ? prep.data : null,
    };
}
/**
 * Whether this tenant's own site takes a booking or takes a request, read from
 * a config document that predates the field — which is **every document
 * written before this wave**.
 *
 * `orderTimingOf`'s shape and `orderTimingOf`'s reason: the store *casts*
 * `tenant_config.config` rather than parsing it, so every reader needs one
 * answer to "what if it is not there, or is not one of the two". `open` is that
 * answer, and it is the one that makes the rollout a no-op — an unreadable
 * value is treated as an unconfigured one, so a hand-edited document can never
 * accidentally *close* a tenant's booking card.
 */
export function guestBookingOf(config) {
    const raw = config?.guest_booking;
    return GuestBooking.safeParse(raw).success ? raw : 'open';
}
/**
 * Who may change the catalog, from a config document that may predate the key.
 *
 * An unreadable or absent value is `owner` — the narrower answer, so a
 * hand-edited document can only ever take a power away from the front desk,
 * never hand one to it.
 */
export function catalogEditorsOf(config) {
    const raw = config?.catalog_editors;
    return CatalogEditors.safeParse(raw).success ? raw : 'owner';
}
/**
 * **May this seat change what the business sells?** — the one place the
 * `CatalogEditors` table is written as code. `core-api`'s route gate and its
 * `GET /v1/session` both call it, so the controls the app draws and the
 * routes that answer cannot disagree.
 *
 * `role` is a plain string so the domain need not import a service's type; any
 * value but the three is refused.
 */
export function mayChangeCatalog(role, editors) {
    if (role === 'owner')
        return true;
    if (role === 'staff')
        return editors === 'owner_and_staff';
    return false;
}
/**
 * The waiver this tenant publishes, or `null` for "asks for none".
 *
 * `orderTimingOf`'s shape — the store casts `tenant_config.config` rather than
 * parsing it — with the **opposite** tolerance, and the difference is the
 * point: where an unreadable `order_timing` falls back to a working default,
 * an unreadable `waiver` falls back to `null`.
 *
 * That is deliberate and it is the safer of the two failures. A malformed
 * waiver block means nobody is asked to tick a box, which loses a record; the
 * alternative — half a document shown as if it were the whole one — would
 * *create* a record of somebody agreeing to words that were never rendered.
 * Losing a record is recoverable by fixing the document and asking again;
 * inventing one is not.
 */
export function waiverOf(config) {
    const parsed = TenantWaiver.safeParse(config?.waiver);
    return parsed.success ? parsed.data : null;
}
/**
 * How long this tenant's unconfirmed requests hold their slot, read from a
 * config document that predates the field.
 *
 * `guestBookingOf`'s shape: the store casts rather than parses, so an absent,
 * unreadable or out-of-range value answers the default rather than throwing.
 * Clamping instead of refusing is deliberate — the failure mode of a refusal
 * here would be a request that could not be *taken* because somebody typed
 * `1000` into a JSON document, and a week is a ceiling rather than a rule.
 */
export function requestHoldHoursOf(config) {
    const raw = config?.['request_hold_hours'];
    if (typeof raw !== 'number' || !Number.isFinite(raw))
        return REQUEST_HOLD_HOURS_DEFAULT;
    const whole = Math.trunc(raw);
    if (whole < 1)
        return REQUEST_HOLD_HOURS_DEFAULT;
    return Math.min(whole, REQUEST_HOLD_HOURS_MAX);
}
/**
 * **The three-mode decision, in one function** — `bookingEntry()`'s server-side
 * twin and the only place the table in `GuestBooking`'s note is executed.
 *
 * `apps/guest-api` refuses an instant booking with it and `apps/site` draws its
 * buttons from its browser-side counterpart; the two agreeing is what stops a
 * page that says "Book" over a door that answers 501. It takes the *client's*
 * flag rather than a session, because the only thing it is allowed to know
 * about a guest is whether the owner has said yes to her.
 */
export function mayBookInstantly(mode, mayBookOnline) {
    if (mode === 'open')
        return true;
    if (mode === 'request')
        return false;
    return mayBookOnline;
}
/**
 * What this shop's guests may do with the shelf, read from a config document
 * that predates the field — which is **every document written before this
 * wave**.
 *
 * `orderTimingOf`'s shape and `orderTimingOf`'s reason, with the tolerance one
 * notch wider: each half is read on its own, so a document that says
 * `{"pickup": true}` and nothing else gets pickup *and* keeps today's posted
 * shop, rather than having the whole object thrown away over a missing key. The
 * answer when there is nothing readable at all is `{ pickup: false, ship: true }`
 * — today's shop, exactly, for every tenant in the fleet.
 */
export function shopFulfilmentOf(config) {
    const raw = config?.['shop_fulfilment'];
    const document = raw === null || typeof raw !== 'object' ? {} : raw;
    return {
        pickup: document['pickup'] === true,
        ship: document['ship'] === undefined ? true : document['ship'] === true,
    };
}
/**
 * Where a pickup reservation at this shop gets paid for.
 *
 * **Absent is not a fixed word**, and that is the only interesting thing about
 * this reader. An explicit value always wins. With nothing written, a tenant
 * that has a usable PayPal rail answers `prepay` and a tenant that has none
 * answers `at_pickup` — because a shop that *cannot* take a payment online must
 * not be configured by omission to demand one, and a shop that can should not
 * stop asking because nobody typed a word into a JSON document.
 *
 * The owner this arrived for sets it explicitly (`at_pickup`), and her reason is
 * the one sentence the whole feature is built around: *"I never want to have
 * money that I haven't earned yet."*
 */
export function shopPaymentOf(config) {
    const raw = config?.['shop_payment'];
    if (ReservationPayment.safeParse(raw).success)
        return raw;
    return paymentsUsable(paymentsConfigOf(config ?? {})) ? 'prepay' : 'at_pickup';
}
/** How many days a ready reservation is held before it expires. `7` when absent. */
export const DEFAULT_PICKUP_EXPIRY_DAYS = 7;
export function pickupExpiryDaysOf(config) {
    const raw = config?.['shop_pickup_expiry_days'];
    if (typeof raw !== 'number' || !Number.isInteger(raw) || raw < 1 || raw > 90) {
        return DEFAULT_PICKUP_EXPIRY_DAYS;
    }
    return raw;
}
/**
 * Which tenders this tenant's till shows, read from a config document that
 * predates the field — which is **every document written before this wave**.
 *
 * `orderTimingOf`'s shape and `orderTimingOf`'s reason, with one addition that
 * is the whole of its tolerance: the list is filtered **element by element**
 * rather than accepted or rejected whole. A document that names five good
 * tenders and one the fleet has never heard of yields the five, because the
 * alternative — throwing the owner's whole choice away over one bad word, or
 * putting an unknown string on a button — is worse than either half of it. An
 * empty result (no field, not a list, nothing recognisable in it) is
 * `DEFAULT_TILL_TENDERS`, so the till always has buttons and they are always
 * today's.
 *
 * Duplicates are collapsed and the owner's order is kept: the order is the
 * order she meets them in at the counter, and sorting it into ours would be
 * rearranging her drawer.
 */
export function tillTendersOf(config) {
    const raw = config?.['till'];
    const list = raw?.['tenders'];
    if (!Array.isArray(list))
        return DEFAULT_TILL_TENDERS;
    const seen = new Set();
    for (const entry of list) {
        if (ExternalTender.safeParse(entry).success)
            seen.add(entry);
    }
    return seen.size === 0 ? DEFAULT_TILL_TENDERS : [...seen];
}
/**
 * **Whether this till asks for a tip.** `false` unless the owner said otherwise.
 *
 * `tillTendersOf`'s shape, with the opposite default argument: an absent list of
 * tenders must still draw buttons, so it falls back to three; an absent answer
 * about tips must *not* invent a prompt, so it falls back to silence. Anything
 * that is not literally `true` is `false`, which covers the document that
 * predates the field, a document with `till: {}` and a document with a string
 * in it.
 */
export function tillTipsEnabled(config) {
    const raw = config?.['till'];
    return raw?.['tips'] === true;
}
/**
 * The tenant's tax block, read from a config document that predates the field —
 * which is **every document written before this wave**.
 *
 * `conciergeConfigOf`'s shape and its reason: the store *casts*
 * `tenant_config.config` rather than parsing it, so every reader needs one
 * answer to "what if it is not there". `null` — not `{}` and not a rate of zero
 * — because "this shop has no tax" and "this shop has a rate of zero" are
 * different facts, and only the first one hides the line.
 *
 * A malformed block reads as `null` rather than throwing: a till that refused to
 * open because somebody typed `"7.25"` into a JSON document would be a till that
 * is down, and the honest fallback is the behaviour every tenant already has.
 * The **write** path is where a bad block is refused, loudly, with a sentence.
 */
export function taxConfigOf(config) {
    const raw = config?.['tax'];
    const parsed = TaxConfig.safeParse(raw);
    return parsed.success ? parsed.data : null;
}
/**
 * The concierge residue, read from a config document that predates the field —
 * which is every document written before this wave.
 *
 * `defaultLocaleOf`'s shape and `defaultLocaleOf`'s reason: the store *casts*
 * `tenant_config.config` rather than parsing it, so every reader needs one
 * answer to "what if it is not there". `null` — not `{}` — so a caller can tell
 * "this tenant has never had the concierge enabled" from "it has, and the
 * widget key is the empty string", which are different facts and only one of
 * them is a bug.
 */
export function conciergeConfigOf(config) {
    const raw = config?.['concierge'];
    const parsed = TenantConciergeConfig.safeParse(raw);
    return parsed.success ? parsed.data : null;
}
/** The tenant's declared `supported_locales`, `[]` when the field is absent. */
export function supportedLocalesOf(config) {
    const raw = config?.supported_locales;
    if (!Array.isArray(raw))
        return [];
    return raw.filter((l) => typeof l === 'string' && Locale.safeParse(l).success);
}
/* ------------------------------------------------------------------ */
/* The engine's own numbers — `yourstore scheduling grid`              */
/* ------------------------------------------------------------------ */
/**
 * The five keys the availability engine reads and nothing else does: the slot
 * grid, the horizon, the hold ceiling, how far `alternatives` may wander, and
 * the pacing cap. `SCHEDULING_POLICY_KEYS`'s shape and its reason — one list,
 * so the CLI verb that maps flags onto it and the store writer that folds it
 * into one statement cannot disagree about what is in it.
 *
 * These had no door at all until 2026-09-22: `createTenant` wrote the three
 * the roster needs and every later change was `psql`. A knob only an
 * operator with a database connection can turn is a knob nobody turns.
 */
export const SCHEDULING_GRID_KEYS = [
    'slot_grid_min',
    'horizon_days',
    'max_hold_seconds',
    'alternatives_window_days',
    'max_starts_per_step',
];
/**
 * What a caller may change about the engine's numbers. `SchedulingPolicyPatch`'s
 * rules, verbatim: `.strict()`, every key optional, **`null` removes the key** —
 * which for the four defaulted keys means "the platform default again"
 * (`schedulingGridOf` answers it) and for the pacing cap means "no cap", the
 * state every tenant that is not a restaurant is in.
 *
 * The bounds are `TenantConfig`'s own, taken from the same schema rather than
 * restated, so a grid this patch accepts is one the document accepts.
 */
export const SchedulingGridPatch = z
    .object({
    slot_grid_min: TenantConfig.shape.slot_grid_min.removeDefault().nullable().optional(),
    horizon_days: TenantConfig.shape.horizon_days.removeDefault().nullable().optional(),
    max_hold_seconds: TenantConfig.shape.max_hold_seconds.removeDefault().nullable().optional(),
    alternatives_window_days: TenantConfig.shape.alternatives_window_days
        .removeDefault()
        .nullable()
        .optional(),
    max_starts_per_step: TenantConfig.shape.max_starts_per_step.nullable().optional(),
})
    .strict();
/**
 * The engine's numbers as the engine sees them: the document's value where it
 * states one, the schema default otherwise. `defaultLocaleOf`'s posture — the
 * store *casts* the document, so a malformed value reads as the default rather
 * than as a crash — and one function rather than four `?? 15`s.
 */
export function schedulingGridOf(config) {
    const raw = (config ?? {});
    const defaults = TenantConfig.parse({});
    const stated = [];
    const read = (key) => {
        const parsed = TenantConfig.shape[key].removeDefault().safeParse(raw[key]);
        if (parsed.success)
            stated.push(key);
        return parsed.success ? parsed.data : defaults[key];
    };
    const grid = {
        slot_grid_min: read('slot_grid_min'),
        horizon_days: read('horizon_days'),
        max_hold_seconds: read('max_hold_seconds'),
        alternatives_window_days: read('alternatives_window_days'),
    };
    const cap = TenantConfig.shape.max_starts_per_step.safeParse(raw['max_starts_per_step']);
    const capValue = cap.success ? (cap.data ?? null) : null;
    if (capValue !== null)
        stated.push('max_starts_per_step');
    return { ...grid, max_starts_per_step: capValue, stated };
}
/* ------------------------------------------------------------------ */
/* The languages — `yourstore tenant locales`                          */
/* ------------------------------------------------------------------ */
/**
 * The two locale keys, **validated together**: a default the tenant does not
 * list among its supported languages is a tenant that answers a guest in a
 * language it says it does not speak. An empty `supported_locales` stays legal
 * and means "undeclared" (`TenantConfig`'s own words), so the check applies
 * only once the list has something in it.
 *
 * Whole rather than a patch, on purpose: the rule is about the pair, and a
 * patch that carried one key could not state it. The verb reads what is
 * stored, lays the flags over it and hands the pair here — the one validator.
 */
export const TenantLocales = z
    .object({
    default_locale: Locale,
    supported_locales: z.array(Locale),
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.supported_locales.length > 0 &&
        !value.supported_locales.includes(value.default_locale)) {
        ctx.addIssue({
            code: 'custom',
            path: ['default_locale'],
            message: `${value.default_locale} is not among the supported locales (${value.supported_locales.join(', ')})`,
        });
    }
    const seen = new Set();
    for (const locale of value.supported_locales) {
        if (seen.has(locale)) {
            ctx.addIssue({
                code: 'custom',
                path: ['supported_locales'],
                message: `${locale} is listed twice`,
            });
        }
        seen.add(locale);
    }
});
//# sourceMappingURL=tenant.js.map